import {
  ProfileGender,
  StoryElementInputKey,
} from '@storees/api-types';

export interface ObservanceEvent {
  id?: string;
  name: string;
  url?: string;
  adult?: boolean;
}

export interface FeaturedObservance {
  id?: string;
  name: string;
  url?: string;
  score: number;
  popularity: number;
  emoji: string;
  tagline: string;
}

export interface StoryElementInput {
  key: StoryElementInputKey;
  name?: string;
  description?: string;
}

export interface ObservanceLookupOptions {
  date: string;
  timezoneId: string;
  countryCode?: string;
}

export interface GenreChoice {
  name: string;
  description: string;
}

const BLOCKED_EVENT_TERMS = [
  'memorial',
  'remembrance',
  'holocaust',
  'genocide',
  'disease',
  'cancer',
  'suicide',
  'funeral',
  'attack',
  'tragedy',
  'mourning',
];

const FEATURED_HIGH_PRIORITY_TERMS = ['international', 'world', 'global'];

const GENRE_DESCRIPTIONS: Record<string, string> = {
  Fantasy:
    'A story set in a magical world with supernatural elements, mythical creatures, and an epic quest where imagination knows no bounds.',
  'Action and Adventure':
    'A fast-paced narrative filled with daring feats, physical challenges, and a courageous hero overcoming dangerous obstacles.',
  Romance:
    'A story centered around a relationship, emotional connection, and the journey of finding and nurturing love despite challenges.',
  'Sci-Fi':
    'A futuristic tale exploring advanced technology, space exploration, time travel, and the impact of scientific discovery on society.',
  Spooky:
    'A spooky tale designed to evoke trepidation through a supernatural entity, psychological alarm, or an unsettling situation.',
  Mystery:
    'An intriguing story involving a puzzle, secret, and investigation where a character works to uncover the truth behind an unusual event.',
  'Fairy Tale':
    'An enchanting story often involving a magical being, wonder, and a moral lesson, typically with a happy ending for a virtuous character.',
  Comedy:
    'A humorous story designed to entertain and evoke laughter through witty dialogue, an amusing situation, and playful characters.',
  'Animal Fantasy':
    'A story featuring animals with human-like qualities embarking on adventures in fantastical settings.',
  'Action/Adventure':
    'A fast-paced narrative filled with daring feats, physical challenges, and a courageous hero overcoming dangerous obstacles.',
  Western:
    'A story set in the American Old West featuring cowboys, outlaws, and frontier life.',
  'Hospital Drama':
    'A dramatic story set in a medical environment exploring life, death, and human resilience.',
  'Fractured Fairy Tale':
    'A reimagined classic fairy tale with unexpected twists, modern perspectives, or humorous alterations.',
  'Spy Thriller':
    'An exciting espionage story featuring secret agents, international intrigue, and high-stakes missions.',
  Folklore:
    'A traditional story passed down through generations, often explaining natural phenomena or teaching cultural values.',
};

const MALE_GENRES = [
  { name: 'Fantasy', weight: 554 },
  { name: 'Action and Adventure', weight: 424 },
  { name: 'Romance', weight: 341 },
  { name: 'Sci-Fi', weight: 281 },
  { name: 'Spooky', weight: 236 },
  { name: 'Mystery', weight: 229 },
  { name: 'Fairy Tale', weight: 212 },
  { name: 'Comedy', weight: 191 },
  { name: 'Animal Fantasy', weight: 146 },
  { name: 'Action/Adventure', weight: 101 },
  { name: 'Western', weight: 92 },
  { name: 'Hospital Drama', weight: 75 },
  { name: 'Fractured Fairy Tale', weight: 75 },
  { name: 'Spy Thriller', weight: 66 },
  { name: 'Folklore', weight: 53 },
];

const FEMALE_GENRES = [
  { name: 'Romance', weight: 406 },
  { name: 'Fantasy', weight: 405 },
  { name: 'Fairy Tale', weight: 260 },
  { name: 'Mystery', weight: 175 },
  { name: 'Spooky', weight: 169 },
  { name: 'Comedy', weight: 141 },
  { name: 'Sci-Fi', weight: 139 },
  { name: 'Action and Adventure', weight: 136 },
  { name: 'Animal Fantasy', weight: 128 },
  { name: 'Action/Adventure', weight: 86 },
  { name: 'Hospital Drama', weight: 56 },
  { name: 'Fractured Fairy Tale', weight: 47 },
  { name: 'Western', weight: 46 },
  { name: 'Folklore', weight: 36 },
  { name: 'Spy Thriller', weight: 26 },
];

const FALLBACK_GENRES: GenreChoice[] = [
  {
    name: 'Fantasy',
    description:
      'A story set in a magical world with supernatural elements, mythical creatures, and an epic quest where imagination knows no bounds.',
  },
  {
    name: 'Action/Adventure',
    description:
      'A fast-paced narrative filled with daring feats, physical challenges, and a courageous hero overcoming dangerous obstacles.',
  },
  {
    name: 'Sci-Fi',
    description:
      'A futuristic tale exploring advanced technology, space exploration, time travel, and the impact of scientific discovery on society.',
  },
  {
    name: 'Romance',
    description:
      'A story centered around a relationship, emotional connection, and the journey of finding and nurturing love despite challenges.',
  },
  {
    name: 'Spooky',
    description:
      'A spooky tale designed to evoke trepidation through a supernatural entity, psychological alarm, or an unsettling situation.',
  },
  {
    name: 'Fairy Tale',
    description:
      'An enchanting story often involving a magical being, wonder, and a moral lesson, typically with a happy ending for a virtuous character.',
  },
  {
    name: 'Mystery',
    description:
      'An intriguing story involving a puzzle, secret, and investigation where a character works to uncover the truth behind an unusual event.',
  },
  {
    name: 'Comedy',
    description:
      'A humorous story designed to entertain and evoke laughter through witty dialogue, an amusing situation, and playful characters.',
  },
];

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

/** Normalizes event titles for equality checks (calendar vs stored campaign name). */
export function normalizeEventNameForMatch(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Meaningful words from an observance title for tagging and fallback copy.
 * Drops generic framing words (e.g. National, Day) — not for story categorization alone.
 */
export function extractSignificantTokens(eventName: string): string[] {
  const genericFraming = new Set([
    'national',
    'international',
    'world',
    'global',
    'day',
    'month',
    'week',
    'of',
    'the',
    'a',
    'an',
    'and',
    'or',
    'for',
    'in',
    'on',
    'at',
    'to',
    'from',
    'first',
    'last',
    'annual',
    'awareness',
    'appreciation',
    'celebration',
    'festival',
    'holiday',
    'observance',
  ]);
  const raw = eventName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/[\s-]+/)
    .filter(Boolean)
    .filter((t) => t.length > 1 && !genericFraming.has(t));
  return [...new Set(raw)];
}

export function generateFallbackTaglineForEvent(eventName: string): string {
  const tokens = extractSignificantTokens(eventName);
  const focus = tokens[0] ?? eventName.replace(/\s+/g, ' ').trim();
  return `Your story is about ${eventName}. Tap to find out what happens with ${focus}.`;
}

function isBlockedEventName(name: string): boolean {
  const normalizedName = normalize(name);
  return BLOCKED_EVENT_TERMS.some((term) => normalizedName.includes(term));
}

function getEventScore(name: string): number {
  const normalizedName = normalize(name);
  let score = 0;

  if (
    FEATURED_HIGH_PRIORITY_TERMS.some((term) =>
      normalizedName.includes(term)
    )
  ) {
    score += 100;
  }

  if (normalizedName.startsWith('national ')) {
    score -= 40;
  }

  if (normalizedName.includes(' day')) {
    score += 5;
  }

  score -= Math.floor(name.length / 20);
  return score;
}

export function filterAndRankObservances(
  events: ObservanceEvent[]
): FeaturedObservance | null {
  const candidates = events
    .filter((event) => Boolean(event.name))
    .filter((event) => !event.adult)
    .filter((event) => !isBlockedEventName(event.name))
    .map((event) => ({
      id: event.id,
      name: event.name,
      url: event.url,
      score: getEventScore(event.name),
      popularity: 50,
      emoji: '✨',
      tagline: "Something curious is happening today. Tap to start your story.",
    }))
    .sort((a, b) => b.score - a.score || a.name.length - b.name.length);

  return candidates[0] ?? null;
}

function weightedPick(
  options: Array<{ name: string; weight: number }>,
  random: () => number
): string {
  const total = options.reduce((sum, option) => sum + option.weight, 0);
  let needle = random() * total;
  for (const option of options) {
    needle -= option.weight;
    if (needle <= 0) {
      return option.name;
    }
  }
  return options[0]!.name;
}

export function selectWeightedGenre(
  gender?: ProfileGender | 'Male' | 'Female' | null,
  random: () => number = Math.random
): GenreChoice {
  if (!gender) {
    return FALLBACK_GENRES[Math.floor(random() * FALLBACK_GENRES.length)]!;
  }

  const normalizedGender = String(gender);
  const weightedGenres =
    normalizedGender === ProfileGender.Male || normalizedGender === 'Male'
      ? MALE_GENRES
      : FEMALE_GENRES;
  const genreName = weightedPick(weightedGenres, random);

  return {
    name: genreName,
    description: GENRE_DESCRIPTIONS[genreName] ?? FALLBACK_GENRES[0]!.description,
  };
}

/** Comma-separated meaningful words from the observance title (generic words like National / Day stripped). */
export function keyTermsFromFeaturedTitle(eventName: string): string {
  const tokens = extractSignificantTokens(eventName);
  if (tokens.length > 0) {
    return tokens.join(', ');
  }
  return eventName.trim();
}

/**
 * Minimal story hints for the daily featured-day flow: key terms + tagline only.
 * Character, narrator, voice, image style, genre, etc. come from the client.
 */
export function buildGenericStoryElements(
  featured: FeaturedObservance
): StoryElementInput[] {
  const keyTerms = keyTermsFromFeaturedTitle(featured.name);
  const tagline = featured.tagline;

  return [
    {
      key: StoryElementInputKey.Theme,
      name: keyTerms,
      description: tagline,
    },
    {
      key: StoryElementInputKey.Length,
      name: 'short',
      description: 'Short',
    },
  ];
}

export function buildGenericPushCopy(featured: FeaturedObservance): {
  title: string;
  body: string;
} {
  return {
    title: `${featured.emoji} ${featured.name}`,
    body: featured.tagline,
  };
}

export function getDateForTimezone(
  timezoneId: string,
  now: Date = new Date()
): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezoneId,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(now);
}

export function mapCountryToObservanceParams(country: string): {
  countryCode?: string;
  timezoneId?: string;
} {
  const normalized = country.toUpperCase();
  if (normalized === 'US') return { countryCode: 'US', timezoneId: 'America/New_York' };
  if (normalized === 'GB') return { countryCode: 'GB', timezoneId: 'Europe/London' };
  if (normalized === 'ES') return { countryCode: 'ES', timezoneId: 'Europe/Madrid' };
  if (normalized === 'FR') return { countryCode: 'FR', timezoneId: 'Europe/Paris' };
  if (normalized === 'CN') return { countryCode: 'CN', timezoneId: 'Asia/Shanghai' };
  if (normalized === 'IN') return { countryCode: 'IN', timezoneId: 'Asia/Kolkata' };
  if (normalized === 'IT') return { countryCode: 'IT', timezoneId: 'Europe/Rome' };
  if (normalized === 'BR') return { countryCode: 'BR', timezoneId: 'America/Sao_Paulo' };
  return {};
}
